/**
 * IK Session: bridges MappingResult → IK solvers.
 *
 * Resolves effector bones from the mapping, builds IK chains,
 * and provides a unified solve interface for viewport interaction.
 */

import { Matrix4, Vector3 } from 'three';

import type { Skeleton } from '../skeleton/core';
import type { MappingResult } from '../recognize/config';
import { solveTwoBoneIK, defaultPoleTarget } from './twoBoneIK';
import { solveCCDIK, resetChainPose, type CCDIKChain } from './ccdIK';

export type EffectorId =
  | 'LeftHand'
  | 'RightHand'
  | 'LeftFoot'
  | 'RightFoot'
  | 'Head'
  | 'Pelvis';

export type Effector = {
  id: EffectorId;
  boneIndex: number;
  chainBoneIndices: number[];
  solverType: 'two-bone' | 'ccd';
  goalPosition: Vector3;
  poleTarget: Vector3;
  active: boolean;
};

export class IKSession {
  skeleton: Skeleton;
  effectors: Map<EffectorId, Effector>;

  constructor(skeleton: Skeleton, mapping: MappingResult) {
    this.skeleton = skeleton;
    this.effectors = new Map();
    this._resolveEffectors(mapping);
  }

  private _resolveEffectors(mapping: MappingResult): void {
    const r2b = mapping.roleToBone;

    this._tryAddLimbEffector('LeftHand', r2b, 'LeftUpperArm', 'LeftLowerArm', 'LeftHand');
    this._tryAddLimbEffector('RightHand', r2b, 'RightUpperArm', 'RightLowerArm', 'RightHand');
    this._tryAddLimbEffector('LeftFoot', r2b, 'LeftUpperLeg', 'LeftLowerLeg', 'LeftFoot');
    this._tryAddLimbEffector('RightFoot', r2b, 'RightUpperLeg', 'RightLowerLeg', 'RightFoot');

    this._tryAddCCDEffector('Head', r2b, mapping);
  }

  private _tryAddLimbEffector(
    id: EffectorId,
    roleToBone: Record<string, string>,
    rootRole: string,
    midRole: string,
    endRole: string,
  ): void {
    const rootName = roleToBone[rootRole];
    const midName = roleToBone[midRole];
    const endName = roleToBone[endRole];
    if (!rootName || !midName || !endName) return;

    const rootIdx = this.skeleton.resolveBoneRef(rootName, {
      namespace: 'internal',
    });
    const midIdx = this.skeleton.resolveBoneRef(midName, {
      namespace: 'internal',
    });
    const endIdx = this.skeleton.resolveBoneRef(endName, {
      namespace: 'internal',
    });
    if (rootIdx === null || midIdx === null || endIdx === null) return;

    const endPos = this.skeleton.getBoneWorldPosition(endIdx);
    const rootPos = this.skeleton.getBoneWorldPosition(rootIdx);
    const midPos = this.skeleton.getBoneWorldPosition(midIdx);

    this.effectors.set(id, {
      id,
      boneIndex: endIdx,
      chainBoneIndices: [rootIdx, midIdx, endIdx],
      solverType: 'two-bone',
      goalPosition: endPos.clone(),
      poleTarget: defaultPoleTarget(rootPos, midPos, endPos),
      active: false,
    });
  }

  private _tryAddCCDEffector(
    id: EffectorId,
    roleToBone: Record<string, string>,
    mapping: MappingResult,
  ): void {
    const chainName = id === 'Head' ? 'Spine' : undefined;
    if (!chainName) return;

    const chainBoneNames = mapping.chains[chainName];
    if (!chainBoneNames || chainBoneNames.length < 2) return;

    const boneIndices: number[] = [];
    for (const ref of chainBoneNames) {
      const idx = this.skeleton.resolveBoneRef(ref, { namespace: 'internal' });
      if (idx !== null) boneIndices.push(idx);
    }
    if (boneIndices.length < 2) return;

    const headName = roleToBone['Head'];
    let effectorIdx = boneIndices[boneIndices.length - 1];
    if (headName) {
      const headIdx = this.skeleton.resolveBoneRef(headName, {
        namespace: 'internal',
      });
      if (headIdx !== null) {
        effectorIdx = headIdx;
        if (!boneIndices.includes(headIdx)) boneIndices.push(headIdx);
      }
    }

    const effectorPos = this.skeleton.getBoneWorldPosition(effectorIdx);

    this.effectors.set(id, {
      id,
      boneIndex: effectorIdx,
      chainBoneIndices: boneIndices,
      solverType: 'ccd',
      goalPosition: effectorPos.clone(),
      poleTarget: new Vector3(),
      active: false,
    });
  }

  setGoal(id: EffectorId, position: Vector3): void {
    const eff = this.effectors.get(id);
    if (eff) {
      eff.goalPosition.copy(position);
      eff.active = true;
    }
  }

  solve(): void {
    for (const eff of this.effectors.values()) {
      if (!eff.active) continue;

      if (eff.solverType === 'two-bone') {
        this._solveTwoBone(eff);
      } else {
        this._solveCCD(eff);
      }
    }
    this.skeleton.computePosedWorldTransforms();
  }

  private _solveTwoBone(eff: Effector): void {
    const [rootIdx, midIdx, endIdx] = eff.chainBoneIndices;
    const rootBone = this.skeleton.bone(rootIdx);
    const midBone = this.skeleton.bone(midIdx);
    const endBone = this.skeleton.bone(endIdx);

    const rootPos = new Vector3().setFromMatrixPosition(rootBone.posedWorld);
    const midPos = new Vector3().setFromMatrixPosition(midBone.posedWorld);
    const endPos = new Vector3().setFromMatrixPosition(endBone.posedWorld);

    const rootParentWorld =
      rootBone.parent !== null
        ? this.skeleton.bone(rootBone.parent).posedWorld
        : this.skeleton.rootParentWorld;
    const midParentInv = new Matrix4().copy(rootBone.posedWorld).invert();

    const result = solveTwoBoneIK(
      rootPos,
      midPos,
      endPos,
      eff.goalPosition,
      eff.poleTarget,
      rootBone.length,
      midBone.length,
      new Matrix4().copy(rootParentWorld).invert(),
      midParentInv,
    );

    if (result) {
      rootBone.poseLocal.copy(result.rootLocal);
      midBone.poseLocal.copy(result.midLocal);
    }
  }

  private _solveCCD(eff: Effector): void {
    const chain: CCDIKChain = {
      boneIndices: eff.chainBoneIndices.slice(0, -1),
      effectorIndex: eff.chainBoneIndices[eff.chainBoneIndices.length - 1],
    };
    solveCCDIK(this.skeleton, chain, eff.goalPosition, { iterations: 10 });
  }

  resetAll(): void {
    for (const eff of this.effectors.values()) {
      eff.active = false;
      const pos = this.skeleton.getBoneWorldPosition(eff.boneIndex);
      eff.goalPosition.copy(pos);
    }
    this.skeleton.resetPoses();
  }

  resetEffector(id: EffectorId): void {
    const eff = this.effectors.get(id);
    if (!eff) return;
    eff.active = false;
    if (eff.solverType === 'ccd') {
      resetChainPose(this.skeleton, {
        boneIndices: eff.chainBoneIndices.slice(0, -1),
        effectorIndex: eff.chainBoneIndices[eff.chainBoneIndices.length - 1],
      });
    } else {
      for (const idx of eff.chainBoneIndices) {
        this.skeleton.bone(idx).poseLocal.identity();
      }
      this.skeleton.computePosedWorldTransforms();
    }
    const pos = this.skeleton.getBoneWorldPosition(eff.boneIndex);
    eff.goalPosition.copy(pos);
  }

  getEffectorWorldPosition(id: EffectorId): Vector3 | null {
    const eff = this.effectors.get(id);
    if (!eff) return null;
    return new Vector3().setFromMatrixPosition(
      this.skeleton.bone(eff.boneIndex).posedWorld,
    );
  }
}

export function buildIKSession(
  skeleton: Skeleton,
  mapping: MappingResult,
): IKSession {
  return new IKSession(skeleton, mapping);
}
